/**
 * MIRRORED FILE: keep byte-identical in Prologue and Tributary.
 *   prologue:  src/components/ProgressBars/LevelTracker/levelHelpers.ts
 *   tributary: src/app/(pages)/contribute/[school]/components/level-tracker/level-helpers.ts
 * Rule: .ai/constitution (contribution progress parity). Change both copies in twin PRs.
 *
 * Pure logic for the level progress tracker (zoomed-in-milestones ARD decisions
 * #3, #4, #11, #12). Imports nothing, so it runs unchanged under Vitest and Jest.
 */

/** Icon keys the tracker supports. Each app maps every key to an icon component. */
export const LEVEL_ICON_KEYS = ['flag', 'book-open', 'party-popper', 'medal'] as const

export type LevelIconKey = (typeof LEVEL_ICON_KEYS)[number]

/** The start circle ($0) always uses this icon. It is not a level. */
export const START_ICON: LevelIconKey = 'flag'

/** Used for a missing or unknown icon key. */
export const FALLBACK_LEVEL_ICON: LevelIconKey = 'party-popper'

/** One entry of the mirrored level definitions: sets the level's position and icon. */
export interface LevelDefinition {
  key: string
  icon?: string
}

/** A level definition plus the text each app supplies for it. */
export interface LevelContent extends LevelDefinition {
  /** Level title without the "Level N:" prefix; each app adds the prefix. */
  title: string
  description?: string
}

/** A level ready to render. */
export interface Level {
  amount: number
  icon: LevelIconKey
  title: string
  description?: string
}

/** One end of the current bracket. `level` is 1-based; 0 is the $0 start. */
export interface LevelBound {
  level: number
  amount: number
  icon: LevelIconKey
}

/** Everything the tracker renders, derived from the received amount and the levels. */
export interface LevelState {
  /** Number of levels (N). */
  count: number
  /** Levels whose amount has been reached. */
  earned: number
  /** Left circle: the level just cleared, or the $0 start. */
  current: LevelBound
  /** Right circle: the next level, or the last level once all are unlocked. */
  next: LevelBound
  /** The right circle is the last level ("to goal", no right stub). */
  isFinal: boolean
  /** Every level is unlocked ("All levels unlocked"). */
  done: boolean
  /** Fill of the current bracket, 0–100. */
  pct: number
  /** Amount left to the right circle, never below 0. */
  remaining: number
  /** Show the faded left stub: at least one level is cleared. */
  showLeftStub: boolean
  /** Show the faded right stub: more levels follow the right circle. */
  showRightStub: boolean
}

export type LevelPopoverTrigger = 'badge' | 'next_circle'
export type LevelPopoverMethod = 'hover' | 'click' | 'focus'

/** Params of the `level_popover_open` analytics event. */
export interface LevelPopoverOpenEvent {
  /** 1-based level number. */
  level: number
  trigger: LevelPopoverTrigger
  method: LevelPopoverMethod
  unlocked: boolean
}

/** Returns the icon key when the tracker supports it, otherwise the fallback icon. */
export function resolveLevelIcon(icon: string | null | undefined): LevelIconKey {
  const known = LEVEL_ICON_KEYS.find((key) => key === icon)
  return known ?? FALLBACK_LEVEL_ICON
}

/**
 * Pairs the goal ladder with level content by position: rung i gets content i.
 * The number of levels is min(rungs, content entries). There is no other cap,
 * so adding content entries (and rungs from the API) adds levels.
 */
export function buildLevels(amounts: readonly number[], content: readonly LevelContent[]): Level[] {
  const count = Math.min(amounts.length, content.length)
  const levels: Level[] = []

  for (let i = 0; i < count; i++) {
    const entry = content[i]
    const level: Level = {
      amount: amounts[i],
      icon: resolveLevelIcon(entry.icon),
      title: entry.title,
    }
    if (entry.description !== undefined) level.description = entry.description
    levels.push(level)
  }

  return levels
}

/**
 * Derives the tracker state for a received amount. Expects levels in ascending
 * amount order (the goal ladder's order). Returns null when there are no levels.
 *
 * The view zooms in on one bracket: from the level just cleared (or $0) to the
 * next level. Once every level is unlocked it stays on the final bracket, full.
 */
export function deriveLevelState(received: number, levels: readonly Level[]): LevelState | null {
  const count = levels.length
  if (count === 0) return null

  const start: LevelBound = { level: 0, amount: 0, icon: START_ICON }
  const bound = (index: number): LevelBound => ({
    level: index + 1,
    amount: levels[index].amount,
    icon: levels[index].icon,
  })

  const earned = levels.filter((level) => received >= level.amount).length
  const done = earned === count

  let current: LevelBound
  if (earned === 0) current = start
  else if (!done) current = bound(earned - 1)
  else current = count >= 2 ? bound(count - 2) : start // stays on the final bracket

  const next = done ? bound(count - 1) : bound(earned)
  const isFinal = next.level === count

  const span = next.amount - current.amount
  let pct: number
  if (span > 0) pct = ((received - current.amount) / span) * 100
  else pct = received >= next.amount ? 100 : 0
  pct = Math.min(100, Math.max(0, pct))

  return {
    count,
    earned,
    current,
    next,
    isFinal,
    done,
    pct,
    remaining: Math.max(0, next.amount - received),
    showLeftStub: earned >= 1,
    showRightStub: !isFinal,
  }
}

/** Whole US dollars with thousands separators, whatever the browser or app locale: `$1,000`. */
export function formatAmount(amount: number): string {
  const whole = Number.isFinite(amount) ? Math.round(amount) : 0
  return `$${whole.toLocaleString('en-US')}`
}

/**
 * Dedupe key for `level_popover_open`: the event is sent at most once per
 * level + trigger + method while the tracker is mounted.
 */
export function levelPopoverEventKey(event: Pick<LevelPopoverOpenEvent, 'level' | 'trigger' | 'method'>): string {
  return `${event.level}:${event.trigger}:${event.method}`
}
